import useMakeSFRequest from '../request/salesforce/make-sf-call';

export interface FileOperationResult {
  success: boolean;
  data?: any;
  error?: string;
}

export const useFileOperations = () => {
  const request = useMakeSFRequest();

  const listFiles = async (parentId: string): Promise<FileOperationResult> => {
    try {
      const response = await request({
        endpointPath: `/api/file/${parentId}`,
        method: "GET",
      });

      if (response.error) {
        return {
          success: false,
          error: response.error.message || 'Failed to fetch files'
        };
      }

      return {
        success: true,
        data: (response as any).files || []
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const downloadFile = async (contentDocumentId: string, contentType: string): Promise<FileOperationResult> => {
    try {
      // For file downloads, we need to bypass the JSON parsing in processAPIResponse
      // and get the raw binary response directly
      const fileHeaders = new Headers();
      fileHeaders.set("Accept", "*/*");
      fileHeaders.set("Content-Type", "application/octet-stream"); // Force binary handling

      // Call the request function and force it to NOT parse as JSON
      // by setting contentType to something other than application/json
      const response = await request({
        endpointPath: `/api/file/${contentDocumentId}`,
        method: "GET",
        contentType: "application/octet-stream", // This prevents JSON parsing
        headers: fileHeaders,
      });



      if (response.error) {
        return {
          success: false,
          error: response.error.message || 'Failed to download file'
        };
      }

      // If response is a Response object (what we want for binary data)
      if (response instanceof Response) {
        return {
          success: true,
          data: response
        };
      }

      // If we got a parsed JSON response instead of raw binary (the problem case)
      return {
        success: false,
        error: 'File download returned parsed JSON instead of binary data'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const uploadFile = async (parentId: string, file: File): Promise<FileOperationResult> => {
    try {
      const response = await request({
        endpointPath: `/api/file/${parentId}?filename=${encodeURIComponent(file.name)}`,
        method: "POST",
        body: file,
        contentType: file.type,
      });

      if (response.error) {
        return {
          success: false,
          error: response.error.message || 'Failed to upload file'
        };
      }

      return {
        success: true,
        data: (response as any).file
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const uploadNewVersion = async (contentDocumentId: string, file: File): Promise<FileOperationResult> => {
    try {
      const response = await request({
        endpointPath: `/api/file/${contentDocumentId}/version?filename=${encodeURIComponent(file.name)}`,
        method: "PATCH",
        body: file,
        contentType: file.type,
      });

      if (response.error) {
        return {
          success: false,
          error: response.error.message || 'Failed to upload new version'
        };
      }

      return {
        success: true,
        data: (response as any).file
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const unlinkFile = async (contentDocumentId: string, parentId: string): Promise<FileOperationResult> => {
    try {
      const response = await request({
        endpointPath: `/api/file/${contentDocumentId}?parent_id=${parentId}`,
        method: "DELETE",
      });

      if (response.error) {
        return {
          success: false,
          error: response.error.message || 'Failed to unlink file'
        };
      }

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const deleteFile = async (contentDocumentId: string, forceDelete: boolean = false): Promise<FileOperationResult> => {
    try {
      const url = `/api/file/${contentDocumentId}` + (forceDelete ? "?force_delete=true" : "");
      const response = await request({
        endpointPath: url,
        method: "DELETE",
      });

      if (response.error) {
        return {
          success: false,
          error: response.error.message || 'Failed to delete file'
        };
      }

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  // Helper function to normalize v2 response structure
  const normalizeFileResponse = (apiResponse: any) => {
    if (apiResponse.file || apiResponse.files) {
      const files = apiResponse.files || [apiResponse.file];
      return files.map((file: any) => ({
        id: file.content_document_id,
        documentId: file.content_document_id,
        versionId: file.content_version_id,
        name: file.title,
        extension: file.extension,
        contentType: file.content_type,
        size: file.size,
        createdDate: file.created_date,
        createdById: file.created_by_id,
        parentId: file.parent_id,
        shareType: file.share_type,
        visibility: file.visibility,
        isLatest: file.is_latest,
        versionNumber: file.version_number
      }));
    }
    return [];
  };

  // Helper function to infer file extension from content type
  const inferExtensionFromContentType = (contentType: string): string | null => {
    if (!contentType) return null;

    const lowerType = contentType.toLowerCase();

    // Common MIME type to extension mappings
    const mimeToExtension: { [key: string]: string } = {
      'application/pdf': 'pdf',
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/gif': 'gif',
      'text/plain': 'txt',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    };

    // Direct MIME type lookup
    if (mimeToExtension[lowerType]) {
      return mimeToExtension[lowerType];
    }

    // Try to extract from simple patterns like "PDF", "PNG", etc.
    if (lowerType === 'pdf') return 'pdf';
    if (lowerType === 'png') return 'png';
    if (lowerType === 'jpg' || lowerType === 'jpeg') return 'jpg';
    if (lowerType === 'gif') return 'gif';
    if (lowerType === 'doc') return 'doc';
    if (lowerType === 'docx') return 'docx';
    if (lowerType === 'xls') return 'xls';
    if (lowerType === 'xlsx') return 'xlsx';
    if (lowerType === 'txt') return 'txt';

    // For unknown types, try to detect based on file content analysis
    // This would require looking at the blob, but for now return null
    return null;
  };

  // Helper function to convert file extensions to proper MIME types
  const convertToMimeType = (contentType: string): string => {
    let mimeType = contentType;
    const fileExtension = mimeType?.toLowerCase();

    // Map common file extensions to proper MIME types
    switch (fileExtension) {
      case 'png':
        mimeType = 'image/png';
        break;
      case 'jpg':
      case 'jpeg':
        mimeType = 'image/jpeg';
        break;
      case 'gif':
        mimeType = 'image/gif';
        break;
      case 'pdf':
        mimeType = 'application/pdf';
        break;
      case 'doc':
        mimeType = 'application/msword';
        break;
      case 'docx':
        mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        break;
      case 'xls':
        mimeType = 'application/vnd.ms-excel';
        break;
      case 'xlsx':
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'txt':
        mimeType = 'text/plain';
        break;
      // If it's already a proper MIME type, leave it as is
      default:
        // Keep original if it already looks like a MIME type (contains '/')
        if (!mimeType?.includes('/')) {
          mimeType = 'application/octet-stream';
        }
        break;
    }

    return mimeType;
  };

  // Helper function to detect file type from blob content
  const detectFileTypeFromBlob = async (blob: Blob): Promise<{ extension: string; mimeType: string } | null> => {
    try {
      // Read the first few bytes to detect file signature
      const arrayBuffer = await blob.slice(0, 16).arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // PDF signature: %PDF
      if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
        return { extension: 'pdf', mimeType: 'application/pdf' };
      }

      // PNG signature: 89 50 4E 47
      if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
        return { extension: 'png', mimeType: 'image/png' };
      }

      // JPEG signature: FF D8 FF
      if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
        return { extension: 'jpg', mimeType: 'image/jpeg' };
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Helper function to create file wrapper with metadata
  const createFileWrapper = async (blob: Blob, file: any) => {
    let mimeType = convertToMimeType(file.content_type);
    let fileName = file.title;

    // If content type is unknown, try to detect from blob content
    if (file.content_type === 'UNKNOWN' || !file.content_type) {
      const detected = await detectFileTypeFromBlob(blob);
      if (detected) {
        mimeType = detected.mimeType;
        if (!fileName.toLowerCase().endsWith(`.${detected.extension}`)) {
          fileName = `${fileName}.${detected.extension}`;
        }
      }
    }
    // If extension is provided and not already in filename, add it
    else if (file.extension && !fileName.toLowerCase().endsWith(`.${file.extension.toLowerCase()}`)) {
      fileName = `${file.title}.${file.extension}`;
    }
    // If no extension provided, check if title already has a file extension
    else if (!file.extension && fileName) {
      const hasExtension = /\.[a-zA-Z0-9]{1,10}$/.test(fileName);
      if (!hasExtension) {
        // Try to infer extension from content type
        const inferredExtension = inferExtensionFromContentType(file.content_type);
        if (inferredExtension) {
          fileName = `${fileName}.${inferredExtension}`;
        }
      }
    }



    const fileData = new File([blob], fileName, {
      type: mimeType,
    });

    // Create a wrapper object that includes File properties and additional metadata
    return {
      // File-like properties
      name: fileData.name,
      type: fileData.type,
      lastModified: fileData.lastModified,
      size: file.size, // Use the accurate size from API
      stream: fileData.stream.bind(fileData),
      arrayBuffer: fileData.arrayBuffer.bind(fileData),
      slice: fileData.slice.bind(fileData),
      text: fileData.text.bind(fileData),
      _originalFile: fileData,

      // Additional metadata
      id: file.content_document_id,
      version: file.version_number,
      isLatest: file.is_latest,
    };
  };

  return {
    listFiles,
    downloadFile,
    uploadFile,
    uploadNewVersion,
    unlinkFile,
    deleteFile,
    normalizeFileResponse,
    convertToMimeType,
    createFileWrapper
  };
};
